// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { Coding, HealthcareService, Organization, Reference, Resource, Schedule } from '@medplum/fhirtypes';
import { POLARIS_ENV_TAG_SYSTEM, SERVICE_TYPE_REFERENCE_EXTENSION_URL } from './constants';
import { getTimezoneExtensionValue, isValidTimezone } from './time';
import type { SchedulingScope, SchedulingTarget } from './types';

export function getTenantAccountRef(orgId: string): Reference<Organization> {
  if (!orgId.trim()) {
    throw new Error('Tenant organization ID is required for scheduling resources');
  }
  return { reference: `Organization/${orgId}` };
}

export function validateScopedResource(
  resource: Resource,
  resourceType: Resource['resourceType'],
  scope: SchedulingScope
): void {
  validateTenantScopedResource(resource, resourceType, scope);

  validateResourceScope(resource, scope, resourceType);
}

export function validateTenantScopedResource(
  resource: Resource,
  resourceType: Resource['resourceType'],
  scope: SchedulingScope
): void {
  if (resource.resourceType !== resourceType) {
    throw new Error(`Expected a ${resourceType} resource`);
  }
  if (!resource.id?.trim()) {
    throw new Error(`${resourceType} ID is required for scheduling operations`);
  }

  const accountReference = getTenantAccountRef(scope.organizationId).reference;
  if (!hasTenantAccount(resource, accountReference)) {
    throw new Error(`${resourceType} must be scoped to ${accountReference}`);
  }
}

export function validateResourceScope(
  resource: Resource,
  scope: SchedulingScope,
  resourceName: string = resource.resourceType
): void {
  const accountReference = getTenantAccountRef(scope.organizationId).reference;
  if (!hasTenantAccount(resource, accountReference)) {
    throw new Error(`${resourceName} must be scoped to ${accountReference}`);
  }

  const environmentTags = (resource.meta?.tag ?? []).filter((tag) => tag.system === POLARIS_ENV_TAG_SYSTEM);
  if (environmentTags.length !== 1 || environmentTags[0].code !== scope.environment) {
    throw new Error(`${resourceName} must be scoped to environment ${scope.environment}`);
  }
}

function hasTenantAccount(resource: Resource, accountReference: string | undefined): boolean {
  // Medplum's scheduling response uses `meta.accounts`; `meta.account` is deprecated.
  return (
    resource.meta?.account?.reference === accountReference ||
    resource.meta?.accounts?.some((account) => account.reference === accountReference) === true
  );
}

export function validateSchedulingTarget(target: SchedulingTarget): void {
  if (!target) {
    throw new Error('A validated HealthcareService and Schedule target is required');
  }
  validateSchedulingScope(target.scope);
  validateScopedResource(target.healthcareService, 'HealthcareService', target.scope);
  validateScopedResource(target.schedule, 'Schedule', target.scope);
  validateActor(target.actor, target.scope);

  const actorReference = `${target.actor.resourceType}/${target.actor.id}`;
  if (target.schedule.actor?.length !== 1 || target.schedule.actor[0].reference !== actorReference) {
    throw new Error(`Schedule must have exactly one actor reference matching ${actorReference}`);
  }

  const timezone = getTimezoneExtensionValue(target.actor.extension);
  if (!timezone || !isValidTimezone(timezone)) {
    throw new Error(`${actorReference} must have exactly one valid IANA timezone extension`);
  }

  validateScheduleServiceType(target.schedule, target.healthcareService);
}

function validateActor(actor: SchedulingTarget['actor'], scope: SchedulingScope): void {
  if (!['Practitioner', 'Location', 'Device'].includes(actor?.resourceType ?? '')) {
    throw new Error('Schedule actor must be a Practitioner, Location, or Device');
  }
  validateScopedResource(actor, actor.resourceType, scope);
}

function codingKey(coding: Coding): string {
  return [coding.system ?? '', coding.version ?? '', coding.code ?? ''].join('|');
}

function validateScheduleServiceType(schedule: Schedule, healthcareService: HealthcareService): void {
  const expectedReference = `HealthcareService/${healthcareService.id}`;
  const references = (schedule.serviceType ?? []).flatMap((serviceType) =>
    (serviceType.extension ?? [])
      .filter((extension) => extension.url === SERVICE_TYPE_REFERENCE_EXTENSION_URL)
      .map((extension) => extension.valueReference?.reference)
  );
  if (references.filter((reference) => reference === expectedReference).length !== 1) {
    throw new Error(`Schedule must include one valid reference to ${expectedReference}`);
  }

  const healthcareServiceCoding = new Set(
    (healthcareService.type ?? []).flatMap((serviceType) => (serviceType.coding ?? []).map(codingKey))
  );
  const scheduleMatchesService = (schedule.serviceType ?? []).some((serviceType) =>
    (serviceType.coding ?? []).some((coding) => healthcareServiceCoding.has(codingKey(coding)))
  );
  if (!healthcareServiceCoding.size || !scheduleMatchesService) {
    throw new Error('Schedule.serviceType must match HealthcareService.type');
  }
}

export function validateSchedulingScope(scope: SchedulingScope): void {
  if (!scope) {
    throw new Error('A validated scheduling scope is required');
  }
  getTenantAccountRef(scope.organizationId);
  getEnvironmentTag(scope.environment);
}

/** Applies the required tenant scope to a v2 scheduling resource. */
export function withTenantScope<T extends Resource>(resource: T, orgId: string): T {
  const account = getTenantAccountRef(orgId);
  const existingAccount = resource.meta?.account;
  if (existingAccount && existingAccount.reference !== account.reference) {
    throw new Error(`Resource is already scoped to tenant ${existingAccount.reference ?? 'an unknown organization'}`);
  }
  return {
    ...resource,
    meta: {
      ...resource.meta,
      account,
    },
  };
}

export function getEnvironmentTag(environment: string): Coding {
  if (!/^(dev-[a-z0-9][a-z0-9-]*|e2e|preview-[a-z0-9][a-z0-9-]*|staging|production)$/.test(environment)) {
    throw new Error(`Invalid Polaris environment: ${environment}`);
  }
  return { system: POLARIS_ENV_TAG_SYSTEM, code: environment };
}

export function withEnvironmentScope<T extends Resource>(resource: T, environment: string): T {
  const environmentTag = getEnvironmentTag(environment);
  const existingEnvironmentTags = (resource.meta?.tag ?? []).filter((tag) => tag.system === POLARIS_ENV_TAG_SYSTEM);
  for (const existingEnvironmentTag of existingEnvironmentTags) {
    if (existingEnvironmentTag.code !== environment) {
      throw new Error(
        `Resource is already scoped to environment ${existingEnvironmentTag.code ?? 'an unknown environment'}`
      );
    }
  }

  const otherTags = (resource.meta?.tag ?? []).filter((tag) => tag.system !== POLARIS_ENV_TAG_SYSTEM);
  return {
    ...resource,
    meta: {
      ...resource.meta,
      tag: [...otherTags, environmentTag],
    },
  };
}

export function withTenantAndEnvironmentScope<T extends Resource>(
  resource: T,
  organizationId: string,
  environment: string
): T {
  return withEnvironmentScope(withTenantScope(resource, organizationId), environment);
}
